import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface Toast { id: string; message: string; type?: 'info' | 'success' | 'error'; leaving?: boolean; }

interface ToastContextValue { addToast: (msg: string, type?: Toast['type']) => void; removeToast: (id: string) => void; }

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    // Mark leaving for animation, then remove
    setToasts(ts => ts.map(t => t.id === id ? { ...t, leaving: true } : t));
    setTimeout(() => {
      setToasts(ts => ts.filter(t => t.id !== id));
    }, 260); // match CSS exit animation duration
  }, []);

  const addToast = useCallback((message: string, type: Toast['type']='info') => {
    const id = Math.random().toString(36).slice(2);
    setToasts(ts => [...ts, { id, message, type }]);
    setTimeout(() => removeToast(id), 3200);
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <div className="toast-container" aria-live="polite" aria-atomic="false">
        {toasts.map(t => (
          <div key={t.id} className={`toast ${t.type || 'info'}${t.leaving ? ' leaving' : ''}`}>{t.message}</div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export function useToast(){
  const ctx = useContext(ToastContext);
  if(!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
